/**
 * Date formatting utilities for consistent DD/MM/YYYY format across the application
 */

/**
 * Format a date to DD/MM/YYYY format
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date string in DD/MM/YYYY format or 'N/A' if invalid
 */
export const formatDate = (dateString) => {
  if (!dateString || dateString === 'N/A' || dateString === '') return 'N/A';
  
  try {
    // If it's in YYYY-MM-DD format, parse it correctly
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Handle DD/MM/YYYY format (backend sends dates in DD/MM/YYYY)
    if (typeof dateString === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        // Backend already sends dates in DD/MM/YYYY format, so return as-is
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      }
    }
    
    // For Date objects or other formats, try to parse
    let date;
    if (dateString instanceof Date) {
      date = dateString;
    } else {
      date = new Date(dateString);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    // Format as DD/MM/YYYY using en-GB locale
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

/**
 * Get current date in DD/MM/YYYY format
 * @returns {string} Current date in DD/MM/YYYY format
 */
export const getCurrentDate = () => {
  return formatDate(new Date());
};

/**
 * Convert DD/MM/YYYY to YYYY-MM-DD format for HTML date inputs
 * @param {string} ddmmyyyy - Date string in DD/MM/YYYY format
 * @returns {string} Date string in YYYY-MM-DD format or empty string if invalid
 */
export const ddmmyyyyToYYYYMMDD = (ddmmyyyy) => {
  if (!ddmmyyyy || ddmmyyyy === 'N/A') return '';
  
  try {
    const parts = ddmmyyyy.split('/');
    if (parts.length !== 3) return '';
    
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  } catch (error) {
    console.error('Error converting date format:', error);
    return '';
  }
};

/**
 * Convert YYYY-MM-DD to DD/MM/YYYY format
 * @param {string} yyyymmdd - Date string in YYYY-MM-DD format
 * @returns {string} Date string in DD/MM/YYYY format or 'N/A' if invalid
 */
export const yyyymmddToDDMMYYYY = (yyyymmdd) => {
  if (!yyyymmdd) return 'N/A';
  
  try {
    const date = new Date(yyyymmdd);
    return formatDate(date);
  } catch (error) {
    console.error('Error converting date format:', error);
    return 'N/A';
  }
};

/**
 * Calculate age in days between two dates
 * @param {string|Date} startDate - Start date (casting date)
 * @param {string|Date} endDate - End date (testing date)
 * @returns {number} Number of days between dates or 0 if invalid
 */
export const calculateDaysDifference = (startDate, endDate) => {
  if (!startDate || !endDate) {
    console.warn('Missing dates for calculation:', { startDate, endDate });
    return 0;
  }
  
  try {
    // Handle DD/MM/YYYY format specifically
    let start, end;
    
    console.log('🔍 DEBUG - Date calculation inputs:', { startDate, endDate });
    
    // Check if dates are in DD/MM/YYYY format
    if (typeof startDate === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(startDate)) {
      const [day, month, year] = startDate.split('/');
      start = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
      console.log('🔍 DEBUG - Parsed start date (DD/MM/YYYY):', { day, month, year, start });
    } else {
      start = new Date(startDate);
      console.log('🔍 DEBUG - Parsed start date (other format):', start);
    }
    
    if (typeof endDate === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(endDate)) {
      const [day, month, year] = endDate.split('/');
      end = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
      console.log('🔍 DEBUG - Parsed end date (DD/MM/YYYY):', { day, month, year, end });
    } else {
      end = new Date(endDate);
      console.log('🔍 DEBUG - Parsed end date (other format):', end);
    }
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.warn('Invalid dates provided:', { startDate, endDate, start, end });
      return 0;
    }
    
    // Calculate difference in milliseconds
    const diffTime = end - start;
    console.log('🔍 DEBUG - Time difference (ms):', diffTime);
    
    // Convert to days and round up to get the age
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    console.log('🔍 DEBUG - Calculated days:', diffDays);
    
    // Ensure we return a positive number (age should never be negative)
    const result = Math.max(0, diffDays);
    console.log('🔍 DEBUG - Final result:', result);
    return result;
  } catch (error) {
    console.error('Error calculating date difference:', error, { startDate, endDate });
    return 0;
  }
};

/**
 * Get date one year from now in DD/MM/YYYY format
 * @returns {string} Date one year from now in DD/MM/YYYY format
 */
export const getDateOneYearFromNow = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return formatDate(date);
};

/**
 * Get date N days from a given date in DD/MM/YYYY format
 * @param {string|Date} fromDate - Starting date
 * @param {number} days - Number of days to add
 * @returns {string} Resulting date in DD/MM/YYYY format
 */
export const addDays = (fromDate, days) => {
  try {
    const date = new Date(fromDate);
    date.setDate(date.getDate() + days);
    return formatDate(date);
  } catch (error) {
    console.error('Error adding days to date:', error);
    return 'N/A';
  }
};

export default {
  formatDate,
  getCurrentDate,
  ddmmyyyyToYYYYMMDD,
  yyyymmddToDDMMYYYY,
  calculateDaysDifference,
  getDateOneYearFromNow,
  addDays
};

